// Адмін: журнал WayForPay-платежів. Якщо callback прийшов без зрозумілого clientAccountId,
// тут видно сирі дані і є кнопка "Активувати для юзера" — задаєш telegram_id і місяці.
import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useFetch } from '../../lib/useFetch';
import { Loader } from '../../components/Loader';
import { ErrorView } from '../../components/ErrorView';

const STATUS_COLOR: Record<string, string> = {
  APPROVED: 'text-tg-success',
  NEEDS_MANUAL: 'text-yellow-500',
  ORPHAN: 'text-yellow-500',
  BAD_SIGNATURE: 'text-tg-destructive',
  DECLINED: 'text-tg-destructive',
  REFUNDED: 'text-tg-hint',
  PENDING: 'text-tg-hint',
};

export function AdminPurchases() {
  // За замовч. ховаємо покинуті checkout'и (EXPIRED/DECLINED) — це шум. «Показати всі» — увімкнути.
  const [show, setShow] = useState<'active' | 'all'>('active');
  const list = useFetch(() => api.admin.listPurchases(show), [show]);

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 flex-1 text-xs text-tg-hint">
          {show === 'active'
            ? 'Активні платежі (покинуті EXPIRED/DECLINED сховано). NEEDS_MANUAL/ORPHAN — активуй вручну.'
            : 'Усі callback\'и, включно з покинутими спробами оплати.'}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => setShow((s) => (s === 'active' ? 'all' : 'active'))}
            className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs"
          >
            {show === 'active' ? 'Показати всі' : 'Лише активні'}
          </button>
          <button onClick={list.reload} className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs">
            ↻
          </button>
        </div>
      </div>

      {list.loading && <Loader />}
      {list.error && <ErrorView error={list.error} onRetry={list.reload} />}

      {list.data?.length === 0 && (
        <p className="card text-sm text-tg-hint">Платежів ще не було.</p>
      )}

      {list.data?.map((p) => (
        <PurchaseCard key={p.id} p={p} onChanged={list.reload} />
      ))}
    </div>
  );
}

type Item = NonNullable<ReturnType<typeof api.admin.listPurchases> extends Promise<infer R> ? R : never>[number];

function PurchaseCard({ p, onChanged }: { p: Item; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [tg, setTg] = useState(p.clientAccountId?.replace(/^tg/i, '') ?? '');
  const [months, setMonths] = useState<number>(p.months || 1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function assign() {
    if (busy) return;
    if (!tg.match(/^\d+$/)) {
      setErr('Введіть тільки числовий Telegram ID');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // Шукаємо юзера за telegramId — для цього є API listUsers, але швидше через окремий пошук.
      // Простіше — нехай адмін введе наш userId. Але юзеру простіше знати tg id.
      // Тому додаємо невеликий лук-ап — listUsers з пошуком по tg id неможливо одразу,
      // але юзер можна знайти через listPurchases.user якщо вже є запис.
      // Альтернатива — admin міг би пошукати в /admin/users; тут робимо просте: прийняти userId напряму.
      // Для зручності: спочатку шукаємо юзера через окремий /admin/users пошук? Замість — скажемо адміну вводити tg id.
      // На бекенді приймаємо userId — отже перетворити tg→userId треба або через нову ручку, або тут через listUsers.
      // Простіше — додати endpoint. Поки що — приймаємо як є userId, якщо ввели не той — буде 404.
      await api.admin.assignPurchase(p.id, tg, months);
      onChanged();
      setOpen(false);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Помилка');
    } finally {
      setBusy(false);
    }
  }

  const statusCls = STATUS_COLOR[p.status] ?? 'text-tg-text';

  return (
    <div className="card space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold ${statusCls}`}>{p.status}</span>
            <span className="text-xs text-tg-hint">{p.amountUah} ₴ · {p.months || '?'} міс</span>
          </div>
          <div className="mt-0.5 truncate font-mono text-[11px] text-tg-hint">{p.orderReference}</div>
        </div>
        <div className="text-right text-[11px] text-tg-hint">
          {new Date(p.createdAt).toLocaleString('uk-UA', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
          })}
        </div>
      </div>

      {p.user ? (
        <div className="text-xs">
          ✓ Юзер: <strong>{p.user.firstName ?? p.user.username ?? p.user.id}</strong>
          {' · tg:'}{p.user.telegramId ?? '—'}
          {p.user.isPremium && <span className="ml-1 text-tg-success">(👑 активний)</span>}
        </div>
      ) : (
        <div className="text-xs text-yellow-500">
          ⚠️ Юзера не визначено
          {p.clientAccountId && <span> (clientAccountId: <code>{p.clientAccountId}</code>)</span>}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setOpen((o) => !o)}
          className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs"
        >
          {open ? 'Сховати' : '⚡ Активувати для юзера'}
        </button>
        <button
          onClick={() => alert(JSON.stringify(p.rawCallback, null, 2))}
          className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs"
        >
          🔍 Сирий callback
        </button>
      </div>

      {open && (
        <div className="space-y-2 rounded-lg bg-tg-bg/40 p-3">
          <p className="text-[11px] text-tg-hint">
            Введи внутрішній User ID (не Telegram ID — внутрішній з таблиці User).
            Знайти можна на сторінці &quot;Користувачі&quot;.
          </p>
          <input
            value={tg}
            onChange={(e) => setTg(e.target.value)}
            placeholder="User ID (число)"
            className="w-full rounded-lg bg-tg-bg px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <input
              type="number"
              min={1}
              max={36}
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              placeholder="Місяців"
              className="w-24 rounded-lg bg-tg-bg px-3 py-2 text-sm"
            />
            <button
              onClick={assign}
              disabled={busy}
              className="btn-primary flex-1"
            >
              {busy ? 'Активація…' : 'Активувати'}
            </button>
          </div>
          {err && <p className="text-xs text-tg-destructive">{err}</p>}
        </div>
      )}
    </div>
  );
}
